import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import { MakeIcon, MakeIconProps } from '@/components/atoms/MakeIcons/MakeIcon';
import DropdownMenuItem, {
  DropdownMenuItemVariants,
} from '@/components/molecules/DropdownMenuItem';
import { ScComponent } from '@/types/interfaces';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import clsx from 'clsx';
import useWindowSize from '@/hooks/useWindowSize';

export function isDropdownMenuGroups(
  options: DropdownMenuOptionType[] | DropdownMenuGroupType[]
): options is DropdownMenuGroupType[] {
  const groupOptions = options as DropdownMenuGroupType[];
  return groupOptions.every((option) => 'category' in option);
}

export type DropdownMenuOptionType = {
  text: string;
  value: string;
  makeIcon?: MakeIconProps;
};
export type DropdownMenuGroupType = {
  category: string;
  index: number;
  menuItems: DropdownMenuOptionType[];
};

export interface DropdownMenuProps extends ScComponent {
  variant?: DropdownMenuItemVariants;
  options: DropdownMenuOptionType[] | DropdownMenuGroupType[];
  value: string[];
  onChange: (value: string[]) => void;
  setValueIndex?: (value: number) => void;
  showCount?: boolean;
  clearAll?: () => void;
  apply?: () => void;
  noOptions?: boolean;
  makes?: string[];
  models?: { [key: string]: string[] };
  title?: string;
  onClose?: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  className = '',
  variant = DropdownMenuItemVariants.SingleSelect,
  options,
  noOptions = false,
  value,
  onChange,
  showCount = true,
  clearAll,
  apply,
  title,
  onClose,
}) => {
  const { isMobile } = useWindowSize();
  const sortedCategories: DropdownMenuGroupType[] | null = useMemo(() => {
    if (isDropdownMenuGroups(options)) {
      return options.sort((a, b) => a.index - b.index);
    }
    return null;
  }, [options]);

  const containerClassName = clsx(
    'flex flex-col justify-between',
    'fixed inset-0 z-[50] w-screen h-screen max-w-none max-h-none overflow-hidden m-0 p-0 rounded-none bg-neutral-0',
    'm:absolute m:top-[calc(100%+4px)] m:left-0 m:right-0 m:w-full m:h-fit m:z-20 m:rounded-small m:shadow-md m:bg-white m:border m:border-neutral-200',
    className
  );

  return (
    <div className={containerClassName}>
      <div className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-neutral-200 bg-neutral-0 px-xl py-l m:hidden">
        {title && <h3 className="text-h3SemiBold text-neutral-900">{title}</h3>}
        {onClose && (
          <button onClick={onClose} className="p-s">
            <XMarkIcon className="size-xl" />
          </button>
        )}
      </div>
      <div
        className={`modal-scroll-bar min-h-[100px] w-full flex-grow overflow-auto p-s`}
      >
        {noOptions && (
          <div className="flex flex-wrap items-center text-microMedium text-red-medium">
            <ExclamationCircleIcon className="mr-xs size-l fill-red-light" />
            No results found
          </div>
        )}

        {!noOptions &&
          sortedCategories &&
          sortedCategories.map((categoryObj, catIndex) =>
            categoryObj?.menuItems?.length > 0 ? (
              <div key={`${categoryObj?.category || ''}-${catIndex}`}>
                <span className="block py-s text-microMedium text-neutral-600">
                  {categoryObj.category}
                </span>
                {categoryObj.menuItems.map((option, index) => (
                  <DropdownMenuItem
                    key={`dropdown-${option.value}-${index}`}
                    text={option.text || ''}
                    variant={variant}
                    selected={value.includes(option.value)}
                    showCount={showCount}
                    onChange={() => {
                      if (
                        variant === DropdownMenuItemVariants.SingleSelect ||
                        variant === DropdownMenuItemVariants.NoSelect
                      ) {
                        onChange([option.value]);
                        if (!isMobile) {
                          onClose?.();
                        }
                      } else if (value.includes(option.value)) {
                        onChange(value.filter((v) => v !== option.value));
                      } else {
                        onChange([...value, option.value]);
                      }
                    }}
                    iconNode={
                      option.makeIcon && (
                        <MakeIcon
                          {...option.makeIcon}
                          className={'mr-s flex h-l w-l'}
                        />
                      )
                    }
                  />
                ))}
              </div>
            ) : null
          )}
        {!noOptions &&
          (!sortedCategories || sortedCategories.length === 0) &&
          options.map((option, index) => {
            const dropdownOption = option as DropdownMenuOptionType;
            return (
              <div key={`dropdown-${dropdownOption.value}-${index}`}>
                <DropdownMenuItem
                  text={dropdownOption.text || ''}
                  variant={variant}
                  selected={value.includes(dropdownOption.value)}
                  showCount={showCount}
                  onChange={() => {
                    if (
                      variant === DropdownMenuItemVariants.SingleSelect ||
                      variant === DropdownMenuItemVariants.NoSelect
                    ) {
                      onChange([dropdownOption.value]);
                      if (!isMobile) {
                        onClose?.();
                      }
                    } else if (value.includes(dropdownOption.value)) {
                      onChange(value.filter((v) => v !== dropdownOption.value));
                    } else {
                      onChange([...value, dropdownOption.value]);
                    }
                  }}
                  iconNode={
                    dropdownOption.makeIcon && (
                      <MakeIcon
                        {...dropdownOption.makeIcon}
                        className={'mr-s flex h-l w-l'}
                      />
                    )
                  }
                />
                {index !== options.length - 1 && (
                  <div className="block h-full border-[1px] border-solid border-neutral-200 m:hidden"></div>
                )}
              </div>
            );
          })}
      </div>
      {isMobile &&
      ((!noOptions && clearAll) ||
        variant === DropdownMenuItemVariants.SingleSelect ||
        variant === DropdownMenuItemVariants.MultiSelect) ? (
        <div className="sticky bottom-0 z-10 flex items-end justify-center gap-s border-t border-neutral-200 bg-neutral-0 px-l py-s">
          <div className="flex-grow">
            <Button
              aria-label="Clear all options"
              variant={ButtonVariants.Secondary}
              onClick={clearAll}
            >
              Clear all
            </Button>
          </div>
          <div className="flex-grow">
            <Button
              aria-label="Apply options"
              variant={ButtonVariants.Primary}
              onClick={apply || onClose}
            >
              Apply
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DropdownMenu;
